// @ts-nocheck
import React, { useState, useEffect, useContext } from 'react';
import Router, { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import Button from '@/components/button';
import SelectInput from '@/components/select-input';
import TextInput from '@/components/text-input';
import Breadcrumbs from '@/components/breadcrumbs';
import { colors } from '@/theme/';
import Fetch from '@/utils/Fetch';
import SettingsMenu from '@/components/settings/menu';
import Helper from '@/utils/helper';
import * as Enums from '@/utils/enums';
import ToastContext from '@/utils/toast-context';
import LookupService from '@/services/lookup/lookup-service';
import LookupSubMenu from '@/components/lookup/sub-menu';
import ConfirmAction from '@/components/modals/confirm-action';
import Storage from '@/utils/storage';
import { createFileRoute } from '@tanstack/react-router'

function LookupPage() {
  const router = useRouter();
  const { id, type } = router.query; // TODO(tanstack-router): verify router.query → useParams/useSearch

  const getTitleForType = (t: any) => {
    const map = {
      customerType: 'Customer Type', customerStatus: 'Customer Status',
      industryType: 'Industry Type', mediaType: 'Media Type',
      queryType: 'Query Type', queryStatus: 'Query Status',
      queryReason: 'Query Reason', designation: 'Designation',
      faultCause: 'Fault Cause', faultCode: 'Fault Code', faultReason: 'Fault Reason',
    };
    return map[t] || 'Fault Cause';
  };
  const getUrlForType = (t: any, itemId: any) => {
    const prefix = {
      customerType: 'CustomerType', customerStatus: 'CustomerStatus',
      industryType: 'IndustryType', mediaType: 'MediaType',
      queryType: 'QueryType', queryStatus: 'QueryStatus',
      queryReason: 'QueryReason', designation: 'Designation',
      faultCause: 'FaultCause', faultCode: 'FaultCode', faultReason: 'FaultReason',
    };
    return `/${prefix[t] || 'FaultCause'}/${itemId}`;
  };
  const title = getTitleForType(type);

  const { data: itemData } = useQuery(
    ['lookupItem', type, id],
    () => Fetch.get({ url: getUrlForType(type, id) }),
    { enabled: !!id && !!type }
  );
  const { data: queryTypesData } = useQuery(
    ['queryTypes'],
    () => Fetch.get({ url: '/QueryType' }),
    { enabled: type === 'queryStatus' }
  );
  const { data: inventoryCategoriesData } = useQuery(
    ['inventoryCategories'],
    () => Fetch.get({ url: '/InventoryCategory' }),
    { enabled: type === 'faultCode' }
  );
  const queryTypes = queryTypesData?.Results ?? null;
  const inventoryCategories = inventoryCategoriesData?.Results ?? null;

  const toast = useContext(ToastContext);

  let currPath = '';
  if (Router.router) {
    currPath = Router.router.asPath;
  }

  const [saving, setSaving] = useState(false);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    if (itemData && !data) {
      setData(itemData);
      setCode(itemData.Code || '');
      setDescription(itemData.Description || '');
      setQueryTypeSearch(itemData.QueryTypeDescription || '');
      setInventoryCategorySearch(itemData.InventoryCategoryDescription || '');
    }
  }, [itemData]);

  const [formIsDirty, setFormIsDirty] = useState(false);
  const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());

  const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);
  const getAccessStatus = () => {
    let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
    if (subscriptionInfo) {
      setAccessStatus(subscriptionInfo.AccessStatus);
    }
  }

  useEffect(() => {
    getAccessStatus();
  }, []);

  useEffect(() => {
    if (accessStatus === Enums.AccessStatus.LockedWithOutAccess) {
      Helper.nextRouter(Router.replace,"/");
    }
  }, [accessStatus]);

  Helper.preventRouteChange(formIsDirty, setFormIsDirty, setConfirmOptions, saveItem);

  function updateData(field: any, value: any) {
    let newDataState = { ...data };
    newDataState[field] = value;
    setData(newDataState);
    setFormIsDirty(true);
  }

  const [code, setCode] = useState('');
  const handleCodeChange = (e: any) => {
    updateData('Code', e.target.value);
    setCode(e.target.value);
    setFormIsDirty(true);
  }

  const [description, setDescription] = useState('');
  const handleDescriptionChange = (e: any) => {
    updateData('Description', e.target.value);
    setDescription(e.target.value);
    setFormIsDirty(true);
  }

  
  const [queryTypeSearch, setQueryTypeSearch] = useState('');
  const setSelectedQueryType = (queryType: any) => {
    updateData('QueryTypeID', queryType.ID);
    setFormIsDirty(true);
  }
  const handleQueryTypeChange = (e: any) => {
    setQueryTypeSearch(e.target.value);
    setFormIsDirty(true);
  }

  
  const [inventoryCategorySearch, setInventoryCategorySearch] = useState('');
  const setSelectedInventoryCategory = (inventoryCategory: any) => {
    updateData('InventoryCategoryID', inventoryCategory.ID);
    setFormIsDirty(true);
  }
  const handleInventoryCategoryChange = (e: any) => {
    setInventoryCategorySearch(e.target.value);
    setFormIsDirty(true);
  }

  const [inputErrors, setInputErrors] = useState<any>({});

  async function saveItem() {
    setSaving(true);

    const { isValid, errors } = LookupService.validate(type, description, (code as any) ? code : null, data.InventoryCategoryID, data.QueryTypeID);
    setInputErrors(errors);

    if (isValid) {
      const dataPut = await Fetch.put({
        url: `/${type}`,
        params: data
      });
      if (dataPut.ID) {
        toast.setToast!!!({
          message: `${title} saved successfully`,
          show: true,
          type: 'success'
        });
        setData(dataPut);
        setFormIsDirty(false);
      }
    } else {
      toast.setToast!!!({
        message: 'There are errors on the page',
        show: true,
        type: 'error'
      });
    }

    setSaving(false);

    return isValid;
  }

  const setType = (t: any) => {
    Helper.nextRouter(Router.push,`/settings/option/list?type=${t}`);
  }

  if (!router.isReady || !data) return null;

  return (
    <div className="settings-container">
      <div className="row">
        <SettingsMenu urlPath={currPath} />
        <LookupSubMenu type={type} setType={setType} />
        <div className="settings-card">
          <div className="row">
            <div className="column">
              <h3>{title} Details</h3>
            </div>
            <div className="column column-end">
              <Button disabled={accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                text={saving ? "Saving" : 'Save'} onClick={saving ? null : saveItem} extraClasses="fit-content no-margin" />
            </div>
          </div>
          {type == 'queryStatus' ?
            <div className="row">
              <div className="column">
                <SelectInput
                  changeHandler={handleQueryTypeChange}
                  label="Query Type"
                  options={queryTypes}
                  placeholder="Select query type"
                  required={true}
                  noInput={true}
                  setSelected={setSelectedQueryType}
                  type="status"
                  value={queryTypeSearch}
                  error={inputErrors.QueryType}
                />
              </div>
            </div>
            : ''
          }
          {type == 'faultCode' ?
            <div className="row">
              <div className="column">
                <SelectInput
                  changeHandler={handleInventoryCategoryChange}
                  label="Inventory Category"
                  options={inventoryCategories}
                  placeholder="Apply to all"
                  required={true}
                  noInput={true}
                  setSelected={setSelectedInventoryCategory}
                  type="status"
                  value={inventoryCategorySearch}
                  error={inputErrors.InventoryCategory}
                />
              </div>
            </div>
            : ''
          }
          {type == 'faultCause' || type == 'faultCode' || type == 'faultReason' ?
            <div className="row">
              <div className="column">
                <TextInput
                  label="Code"
                  changeHandler={handleCodeChange}
                  required={true}
                  value={code}
                  error={inputErrors.Code}
                />
              </div>
            </div>
            : ''
          }
          <div className="row">
            <div className="column">
              <TextInput
                label="Description"
                changeHandler={handleDescriptionChange}
                required={true}
                value={description}
                error={inputErrors.Description}
              />
            </div>
          </div>
        </div>
      </div>

      <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

      <style jsx>{`
        .row {
          display: flex;
        }
        .column {
          display: flex;
          flex-basis: 0;
          flex-direction: column;
          flex-grow: 1;
        }
        .column :global(.textarea-container) {
          height: 100%;
        }
        .column + .column {
          margin-left: 1.25rem;
        }
        .column-end {
          align-items: flex-end;
        }
        .actions {
          display: flex;
        }
        .actions :global(.button) {
          margin-left: 0.5rem;
          margin-top: 0;
          padding: 0 1rem;
          white-space: nowrap;
        }
        .item {
          color: ${colors.bluePrimary};
          font-weight: bold;
        }
      `}</style>
    </div>
  );
}

export default LookupPage

export const Route = createFileRoute('/_authenticated/settings/option/$id')({ component: LookupPage })
